import React, { useEffect, useRef, useState } from 'react';

import { GenAiSpark } from 'assets/svg/landing/landingIcons';
import LandingSearchPanel from 'components/organism/Landing/LandingSearchPanel';
import { RGM_HOME_HERO, RGM_HOME_SEARCH_INPUT } from 'constants/landingTestIds';
import { extractEmailAndName } from 'utils/helperMethods';

export type LandingCopy = {
    greeting?: string;
    headline?: string;
    headlineAccent?: string;
    searchPlaceholder?: string;
};

interface Props {
    copy: LandingCopy;
    /** Gates the AI Mode switch — see `LandingSearchPanel`. */
    isAskAsperEnabled?: boolean;
}

/**
 * `extractEmailAndName` catches its own failure and returns this literal, so an unusual or
 * missing email would otherwise greet the user as "User". Treat it as "no name".
 */
const NO_NAME_SENTINEL = 'User Name';

const resolveFirstName = (): string => {
    const fullName = extractEmailAndName();
    if (!fullName || fullName === NO_NAME_SENTINEL) return '';
    return fullName.split(' ')[0];
};

/**
 * Greeting, headline and the search field.
 *
 * The collapsed field is swapped for a same-height spacer while the panel is open rather than
 * hidden behind it, so `RGM_HOME_SEARCH_INPUT` always resolves to the one input the user can
 * actually type in, and the box the panel overlays still holds its place in the layout — opening
 * the search never shifts the page beneath it.
 */
function LandingHero({ copy, isAskAsperEnabled = false }: Props) {
    const firstName = resolveFirstName();
    const greeting = firstName ? `${copy.greeting}, ${firstName}` : copy.greeting;

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    // Held here, not in the panel, so collapsing the search does not silently reset the choice.
    const [isAiMode, setIsAiMode] = useState(false);

    useEffect(() => {
        if (isExpanded) inputRef.current?.focus();
    }, [isExpanded]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                setIsExpanded(true);
                // Covers the already-open case, which does not re-run the focus effect above.
                inputRef.current?.focus();
                return;
            }
            if (event.key === 'Escape') setIsExpanded(false);
        };

        const handleMouseDown = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setIsExpanded(false);
        };

        window.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleMouseDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, []);

    // Any edit drops back out of the results view, so the suggestions keep tracking what is typed.
    const handleQueryChange = (value: string) => {
        setQuery(value);
        setIsSubmitted(false);
    };

    return (
        <div data-testid={RGM_HOME_HERO} className="mx-auto flex w-full max-w-[898px] flex-col items-center gap-5 pt-8">
            <div className="flex w-full flex-col items-center gap-1 break-words">
                <p className="w-full text-center font-lg font-normal leading-8 text-navV2TextMuted opacity-80">
                    {greeting}
                </p>
                <div className="flex items-center gap-2 whitespace-nowrap font-xl font-bold leading-[normal]">
                    <span className="text-filterV2TextPrimary">{copy.headline}</span>
                    <span className="landing-headline-accent">{copy.headlineAccent}</span>
                </div>
            </div>

            <div ref={containerRef} className="relative w-full">
                {isExpanded ? (
                    <>
                        <div className="h-14 w-full" aria-hidden />
                        <LandingSearchPanel
                            query={query}
                            isSubmitted={isSubmitted}
                            placeholder={copy.searchPlaceholder ?? ''}
                            inputRef={inputRef}
                            isAskAsperEnabled={isAskAsperEnabled}
                            isAiMode={isAiMode}
                            onQueryChange={handleQueryChange}
                            onSubmit={() => setIsSubmitted(true)}
                            onAiModeChange={setIsAiMode}
                        />
                    </>
                ) : (
                    <div className="flex h-14 w-full items-center rounded-[15px] border border-white bg-white px-5 shadow-landingSearch">
                        <span className="flex size-6 shrink-0 items-center justify-center">
                            <GenAiSpark className="size-5" />
                        </span>
                        <input
                            data-testid={RGM_HOME_SEARCH_INPUT}
                            type="text"
                            readOnly
                            aria-label={copy.searchPlaceholder}
                            placeholder={copy.searchPlaceholder}
                            value={query}
                            onFocus={() => setIsExpanded(true)}
                            onClick={() => setIsExpanded(true)}
                            className="ml-4 w-full cursor-text border-none bg-transparent font-lg text-filterV2TextPrimary outline-none placeholder:text-navV2TextMuted"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default LandingHero;
